import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ACCOUNT } from '@/constants/testIds';
import { asArray } from '@/lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

// Import newly created components
import AccountHeader from '@/components/Account/AccountHeader';
import AddressesTab from '@/components/Account/AddressesTab';
import AddressModal from '@/components/Account/AddressModal';
import OrdersTab from '@/components/Account/OrdersTab';
import ProfileTab from '@/components/Account/ProfileTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Account = () => {
  const { user, token, loading, fetchUser } = useAuth();
  const [orders, setOrders] = useState([]);
  
  // Address Modal State
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const [addressForm, setAddressForm] = useState({ id: null, city_id: '', street: '', building: '', phone: '', details: '', is_default: false });
  const [addressSubmitting, setAddressSubmitting] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = ['addresses', 'orders', 'profile'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'addresses';

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      fetchOrders();
    }
  }, [user, loading]);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/cities`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setCities(res.data.data))
        .catch(err => console.error(err));
    }
  }, [token]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(asArray(response.data?.data));
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  // Address Actions
  const handleOpenAddressModal = (address = null) => {
    if (address) {
      setAddressForm({
        id: address.id,
        city_id: address.city_id || '',
        street: address.street || '',
        building: address.building || '',
        phone: address.phone || '',
        details: address.details || '',
        is_default: address.is_default
      });
    } else {
      setAddressForm({ id: null, city_id: '', street: '', building: '', phone: '', details: '', is_default: false });
    }
    setIsAddressModalOpen(true);
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    if (!addressForm.city_id || !addressForm.street) {
      toast.error('الرجاء إدخال المحافظة والشارع');
      return;
    }
    setAddressSubmitting(true);
    try {
      if (addressForm.id) {
        await axios.put(`${API}/addresses/${addressForm.id}`, addressForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('تم تحديث العنوان بنجاح');
      } else {
        await axios.post(`${API}/addresses`, addressForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('تم إضافة العنوان بنجاح');
      }
      setIsAddressModalOpen(false);
      await fetchUser();
    } catch (error) {
      toast.error('حدث خطأ أثناء حفظ العنوان');
      console.error(error);
    } finally {
      setAddressSubmitting(false);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا العنوان؟')) return;
    try {
      await axios.delete(`${API}/addresses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم حذف العنوان بنجاح');
      await fetchUser();
    } catch (error) {
      toast.error('حدث خطأ أثناء الحذف');
    }
  };

  const handleSetDefaultAddress = async (id) => {
    try {
      await axios.put(`${API}/addresses/${id}/default`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم تعيين كعنوان افتراضي');
      await fetchUser();
    } catch (error) {
      toast.error('حدث خطأ أثناء العملية');
    }
  };

  if (loading) return <div className="py-12 text-center text-lg font-cairo">جاري التحميل...</div>;
  if (!user) return null;

  return (
    <div data-testid={ACCOUNT.dashboard} className="py-8 bg-gray-50/50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1200px]">
        <h1 className="text-3xl md:text-4xl font-tajawal font-bold mb-6 md:mb-8 text-brand-black">حسابي</h1>

        <AccountHeader user={user} />

        <Tabs dir="rtl" defaultValue={initialTab} className="w-full">
          <TabsList className="flex w-full h-auto justify-start md:justify-center overflow-x-auto gap-2 bg-transparent mb-6 p-0 pb-2 no-scrollbar">
            <TabsTrigger className="shrink-0 rounded-full px-4 py-2 md:px-6 md:py-3 text-sm md:text-base font-cairo font-semibold data-[state=active]:bg-burgundy-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all whitespace-nowrap" value="addresses">عناويني</TabsTrigger>
            <TabsTrigger className="shrink-0 rounded-full px-4 py-2 md:px-6 md:py-3 text-sm md:text-base font-cairo font-semibold data-[state=active]:bg-burgundy-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all whitespace-nowrap" data-testid={ACCOUNT.ordersTab} value="orders">طلباتي</TabsTrigger>
            <TabsTrigger className="shrink-0 rounded-full px-4 py-2 md:px-6 md:py-3 text-sm md:text-base font-cairo font-semibold data-[state=active]:bg-burgundy-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all whitespace-nowrap" data-testid={ACCOUNT.profileTab} value="profile">الملف الشخصي</TabsTrigger>
          </TabsList>

          <TabsContent value="addresses" className="mt-2">
            <AddressesTab 
              user={user} 
              cities={cities}
              handleOpenAddressModal={handleOpenAddressModal}
              handleSetDefaultAddress={handleSetDefaultAddress}
              handleDeleteAddress={handleDeleteAddress}
            />
          </TabsContent>

          <TabsContent value="orders" className="mt-2">
            <OrdersTab orders={orders} />
          </TabsContent>

          <TabsContent value="profile" className="mt-2">
            <ProfileTab user={user} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Address Modal */}
      {isAddressModalOpen && (
        <AddressModal 
          addressForm={addressForm}
          setAddressForm={setAddressForm}
          cities={cities}
          handleSaveAddress={handleSaveAddress}
          setIsAddressModalOpen={setIsAddressModalOpen}
          addressSubmitting={addressSubmitting}
        />
      )}
    </div>
  );
};

export default Account;
